import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FragmentLayerDTO,
  IngredientStockDTO,
  LayerTypeDTO,
  ProteinLayerDTO,
  ScreenDTO,
  ScreenLayerDTO,
  ScreenLotDTO,
  LayerStateDTO,
} from 'business/models/Models';
import { SeedLayerDTO, TSelectIngredient } from 'business/models/CustomModels';
import ELayersView from 'enums/ELayersView';
import EditIngredients from './EditIngredients';
import SelectIngredients, {
  ingredientTypesEnum,
} from 'components/customComponents/Layers/SelectIngredients';
import SelectStocks from 'components/customComponents/Layers/SelectStocks';
import LayerInfoParent from './LayerInfoParent';
import LayersIngredients from './LayersIngredients';
import RMLayersTabs from 'components/customComponents/RMLayersTabs';
import ExcludeIngredients from 'components/commandpanel/RandomLayer/ExcludeIngredient';
import EditGroup from '../RandomLayer/Group/EditGroup';
import SelectSeedList from '../SeedingLayer/SelectSeedList';
import SelectProteinList from '../ProteinLayer/SelectProteinList';
import EditProtein from '../ProteinLayer/EditProtein';
import EditSeed from '../SeedingLayer/EditSeed';
import { useRMSelector, useRMDispatch } from 'redux/hooks';
import {
  GetIngredients,
  GetScreenById,
  GetSeedList,
  isNullOrUndefined,
  LayerDesignTabs,
} from 'utils/Global';
import SelectScreen from 'components/customComponents/Layers/SelectScreen';
import EditScreen from '../ScreenLayer/EditScreen';
import LayersSelectionParent from './LayersSelectionParent';
import SelectProteinTreePath from '../ProteinLayer/SelectProteinTreePath';
import {
  setSelectedLayerId,
  setShouldShowLayerEditPanel,
  setShouldShowLayerIngredientPanel,
} from 'redux/reducers/experimentDataReducer';
import {
  selectExp,
  selectExperimentLayers,
  selectSelectedLayer,
  selectShouldShowLayerEditPanel,
  selectShouldShowLayerIngredientPanel,
} from 'redux/selectors/ExperimentStateSelector';
import { ITabItem, withTabBackButton, WithTabBackButtonStateProps } from 'components/tab';
import { BaseSidePanel } from 'components/sidePanel';
import { verifyLayerDetails } from 'utils/ExperimentUtils';
import { cloneDeep, isArray } from 'lodash';
import SelectFragmentScreen from '../FragmentLayer/SelectFragmentScreen';
import SelectFragmentTreePath from '../FragmentLayer/SelectFragmentTreePath';
import { EditFragment } from '../FragmentLayer/EditFragment';
import { useLastSelectedWellNavigationDetails } from 'redux/hooks/experiment';
import { useEagerLoadTreeNodes, useLayerBroadcastReceiver } from './hooks';
import { LayersExplorer } from './LayersExplorer';
import { EPanelHeaderVariant } from 'components/sidePanel/baseSidePanel';
import { useLayerDesignSelectedTabIndex } from 'redux/hooks/layers';
import {
  ELayerDesignSelectedTabIndex,
  setLayerDesignSelectedTabIndex,
} from 'redux/reducers/layerReducer';
import { LayersContainerContextProvider } from './LayersExplorerContextProvider';
import { TreeControlDefaultState, TreeControlState } from 'components/explorer';

const stack: string[] = [];
const mapLayerTypeToContentView = {
  [LayerTypeDTO.Grid]: ELayersView.SELECTINGREDIENTSVIEW,
  [LayerTypeDTO.Protein]: ELayersView.SELECTPROTEIN,
  [LayerTypeDTO.Random]: ELayersView.EDITGROUPSVIEWADD,
  [LayerTypeDTO.Screen]: ELayersView.SELECTSCREEN,
  [LayerTypeDTO.Seed]: ELayersView.SELECTSEEDS,
  [LayerTypeDTO.Fragment]: ELayersView.SELECTFRAGMENTSCREEN,
};

interface Props extends WithTabBackButtonStateProps {
  selectedContentView?: ELayersView;
}

function LayersContainer(props: Props) {
  const { selectedContentView, setHandleOnTabBackPress } = props;

  const [contentView, setContentViewState] = useState<string>(
    selectedContentView || ELayersView.LAYERDESIGNVIEW,
  );

  const [selectIngredient, setSelectIngredient] = useState<TSelectIngredient>({
    name: 'Select Ingredient',
    id: 0,
  });
  const [selectStock, setSelectStock] = useState<IngredientStockDTO>();
  const [highPHStock, setHighPHStock] = useState();
  const dispatch = useRMDispatch();
  const [selectedTab, setSelectedTab] = useState(LayerDesignTabs.Protein);
  const [mainPanelHeading, setMainPanelHeading] = useState('Layers Design');
  const [editIngredientData, setEditIngredientData] = useState(undefined);
  const [editSeedData, setEditSeedData] = useState(undefined);
  const [editExcludeGroupId, setEditExcludeGroupId] = useState<number>(0);
  const [randomLyrActiveGroupId, setRandomLyrActiveGroupId] = useState<number>(-1);
  const [selectedSeed, setSelectedSeed] = useState();
  const [selectedScreen, setSelectedScreen] = useState<ScreenDTO | undefined>(undefined);
  const [selectedIngType, setSelectedIngType] = useState(ingredientTypesEnum.All);
  const _layers = useRMSelector(selectExperimentLayers);
  const _selectedLayer = useRMSelector(selectSelectedLayer);
  const [isStayOnEditGroup, setIsStayOnEditGroup] = useState(false);
  const shouldShowLayersIngredientPanel = useRMSelector(
    selectShouldShowLayerIngredientPanel,
  );
  const shouldShowLayersEditPanel = useRMSelector(selectShouldShowLayerEditPanel);
  const selectedTabIndex = useLayerDesignSelectedTabIndex();
  const _setLayerDesignSelectedTabIndex = (val) =>
    dispatch(setLayerDesignSelectedTabIndex(val));
  const [selectedProteinLayerDetail, setSelectedProteinLayerDetail] =
    useState<ProteinLayerDTO>(
      _selectedLayer?.type === LayerTypeDTO.Protein
        ? (cloneDeep(_selectedLayer.layerDetail) as ProteinLayerDTO)
        : null,
    );
  const [selectedFragmentLayerDetail, setSelectedFragmentLayerDetail] =
    useState<FragmentLayerDTO>(
      _selectedLayer?.type === LayerTypeDTO.Fragment
        ? (cloneDeep(_selectedLayer.layerDetail) as FragmentLayerDTO)
        : null,
    );

  const shouldReturnToLayersExplorerRef = useRef<number | null>(null);

  function setContentView(val) {
    setContentViewState(val);
    if (
      val !== ELayersView.LAYERDESIGNVIEW &&
      selectedTabIndex !== ELayerDesignSelectedTabIndex.Layers
    ) {
      _setLayerDesignSelectedTabIndex(ELayerDesignSelectedTabIndex.Layers);
    }
    stack.push(val);
  }

  const [selectedSeedName, setSelectedSeedName] = useState();
  const [selectedLotId, setSelectedLotId] = useState<number | undefined>();
  const [parentNode, setParentNode] = useState(undefined);
  const { treeControlState, setTreeControlState } = useEagerLoadTreeNodes();
  const [pickerTreeControlState, setPickerTreeControlState] = useState<TreeControlState>(
    TreeControlDefaultState,
  );
  const [selectedFragmentDeleted, setSelectedFragmentDeleted] = useState<boolean>(false);
  const [selectedProteinDeleted, setSelectedProteinDeleted] = useState<boolean>(false);
  const [selectedSeedDeleted, setSelectedSeedDeleted] = useState<boolean>(false);
  const exp = useRMSelector(selectExp);
  const wellNavigationDetails = useLastSelectedWellNavigationDetails();
  const { plateID } = wellNavigationDetails || {};

  const handleSelectedFragmentLayerDetailChange = useCallback((value) => {
    setSelectedFragmentLayerDetail(value);
    setSelectedFragmentDeleted(false);
  }, []);

  const handleSelectedProteinLayerDetailChange = useCallback((value) => {
    setSelectedProteinLayerDetail(value);
    setSelectedProteinDeleted(false);
  }, []);

  const handleEditSeedDataChange = useCallback((value) => {
    setEditSeedData(value);
    setSelectedSeedDeleted(false);
  }, []);

  useLayerBroadcastReceiver({
    treeNodes: treeControlState.treenodes,
    selectedScreen,
    setSelectedScreen,
    selectedFragmentLayerDetail,
    setSelectedFragmentLayerDetail: handleSelectedFragmentLayerDetailChange,
    editSeedData,
    setEditSeedData: handleEditSeedDataChange,
    selectedSeedName,
    setSelectedSeedName,
    selectedProteinLayerDetail,
    setSelectedProteinLayerDetail: handleSelectedProteinLayerDetailChange,
    onFragmentDeleted: () => setSelectedFragmentDeleted(true),
    onProteinDeleted: () => setSelectedProteinDeleted(true),
    onSeedDeleted: () => setSelectedSeedDeleted(true),
  });

  const plate = useMemo(() => {
    if (!exp.plateDto) {
      return;
    }
    const plate = exp.plateDto.find((pl) => pl.plateId === plateID);
    if (plate === null || plate === undefined) {
      return exp.plateDto[0];
    }
    return plate;
  }, [exp.plateDto, plateID]);

  function resetStocksValue() {
    setSelectStock(null);
    setHighPHStock(null);
  }

  function handleLayerType(layerId) {
    dispatch(setSelectedLayerId(layerId));
  }

  const handleReturnToLayersExplorer = useCallback(() => {
    if (shouldReturnToLayersExplorerRef.current === _selectedLayer?.id) {
      shouldReturnToLayersExplorerRef.current = null;
      _setLayerDesignSelectedTabIndex(ELayerDesignSelectedTabIndex.Explorer);
    }
  }, [_selectedLayer?.id, _setLayerDesignSelectedTabIndex]);

  useEffect(() => {
    // To Do - Need to implement these conditions in proper way...
    if (contentView === ELayersView.SELECTSCREEN) {
      setMainPanelHeading('Select Screen');
    } else if (contentView === ELayersView.EDITSCREEN) {
      setMainPanelHeading('Edit Screen');
    } else if (contentView === ELayersView.LAYERDESIGNVIEW) {
      setMainPanelHeading('Layers Design');
      const isLayerWithData = verifyLayerDetails(_selectedLayer);
      if (
        _selectedLayer &&
        shouldShowLayersIngredientPanel &&
        ((isLayerWithData !== 'notfound' && !isLayerWithData) ||
          (isArray(isLayerWithData) && isLayerWithData?.length === 0))
      ) {
        setSelectedTab(0);
      }
    } else if (
      contentView === ELayersView.EDITINGREDIENTSVIEW ||
      contentView === ELayersView.EDITINGREDIENTSVIEWCUSTOM
    ) {
      setMainPanelHeading('Edit Ingredient');
    } else if (
      contentView === ELayersView.SELECTINGREDIENTSVIEW ||
      contentView === ELayersView.SELECTINGREDIENTSVIEWCUSTOM
    ) {
      setMainPanelHeading('Select Ingredients');
    } else if (
      contentView === ELayersView.EDITGROUPSVIEWADD ||
      contentView === ELayersView.EDITGROUPSVIEWEDIT
    ) {
      setMainPanelHeading('Group');
    } else if (contentView === ELayersView.SELECTPROTEIN) {
      setMainPanelHeading('Select Protein');
    } else if (contentView === ELayersView.EDITPROTEIN) {
      setMainPanelHeading('Edit Protein');
    } else if (contentView === ELayersView.PROTEINTREEPATH) {
      setMainPanelHeading('Select Protein Path');
    } else if (contentView === ELayersView.EDITSEED) {
      setMainPanelHeading('Edit Seed');
    } else if (contentView === ELayersView.SELECTSEEDS) {
      setMainPanelHeading('Select Seed');
    } else if (contentView === ELayersView.HIGHPHVIEW) {
      setMainPanelHeading('Select High pH Stock');
    } else if (contentView === ELayersView.SELECTSTOCKSVIEW) {
      if (selectedIngType === ingredientTypesEnum.Buffer) {
        setMainPanelHeading('Select Low pH Stock');
      } else {
        setMainPanelHeading('Select Stock');
      }
    } else if (contentView === ELayersView.SELECTFRAGMENTSCREEN) {
      setMainPanelHeading('Select Fragment');
    } else if (contentView === ELayersView.FRAGMENTTREEPATH) {
      setMainPanelHeading('Select Fragment Location');
    } else if (contentView === ELayersView.EDITFRAGMENT) {
      setMainPanelHeading('Edit Fragment');
    } else {
      setMainPanelHeading(
        `Select Stocks (${
          isNullOrUndefined(selectIngredient) ? '' : selectIngredient.name
        })`,
      );
    }
  }, [contentView, shouldShowLayersIngredientPanel]);

  const disableLayersIngredientsPanel = useCallback(() => {
    dispatch(setShouldShowLayerIngredientPanel(false));
  }, []);

  useEffect(
    () => () => {
      disableLayersIngredientsPanel();
    },
    [],
  );

  useEffect(() => {
    if (_selectedLayer?.type === LayerTypeDTO.Screen) {
      const screenLyr = _selectedLayer.layerDetail as ScreenLayerDTO;
      setSelectedScreen(GetScreenById(screenLyr.screenLot?.experimentId));
      setSelectedLotId(screenLyr.screenLot?.id);
    }

    const isLayerWithData = verifyLayerDetails(_selectedLayer);

    const isNewlyAddedLayer =
      _selectedLayer &&
      (_selectedLayer.layerState === LayerStateDTO.New || _selectedLayer.id < 0);

    const isFromLayersExplorer =
      isNewlyAddedLayer &&
      shouldShowLayersEditPanel &&
      (_selectedLayer?.type === LayerTypeDTO.Protein ||
        _selectedLayer?.type === LayerTypeDTO.Fragment ||
        _selectedLayer?.type === LayerTypeDTO.Seed);

    if (isFromLayersExplorer && _selectedLayer) {
      shouldReturnToLayersExplorerRef.current = _selectedLayer.id;
    }

    if (
      shouldShowLayersEditPanel &&
      (_selectedLayer?.type === LayerTypeDTO.Protein ||
        _selectedLayer?.type === LayerTypeDTO.Fragment ||
        _selectedLayer?.type === LayerTypeDTO.Seed)
    ) {
      dispatch(setShouldShowLayerEditPanel(false));
      if (_selectedLayer?.type === LayerTypeDTO.Protein) {
        setContentView(ELayersView.EDITPROTEIN);
      } else if (_selectedLayer?.type === LayerTypeDTO.Fragment) {
        setContentView(ELayersView.EDITFRAGMENT);
      } else if (_selectedLayer?.type === LayerTypeDTO.Seed) {
        const seedLayerDTO = cloneDeep(_selectedLayer.layerDetail) as SeedLayerDTO;
        seedLayerDTO.startSeedDropVolume = null;
        seedLayerDTO.stopSeedDropVolume = null;
        const globalSeeds = GetSeedList();
        const seed = globalSeeds.find((seed) => seed.id === seedLayerDTO.seedId);
        seedLayerDTO.seedName = seed.name;
        seedLayerDTO.seedDropVolumeVaryAlong = 1;
        handleEditSeedDataChange(seedLayerDTO);
        setSelectedSeedName(seed.name);
        setContentView(ELayersView.EDITSEED);
      }
    } else if (
      _selectedLayer &&
      shouldShowLayersIngredientPanel &&
      ((isLayerWithData !== 'notfound' && !isLayerWithData) ||
        (isArray(isLayerWithData) && isLayerWithData?.length === 0))
    ) {
      setEditIngredientData({});
      setContentView(mapLayerTypeToContentView[_selectedLayer.type]);
      if (
        mapLayerTypeToContentView[_selectedLayer.type] === ELayersView.LAYERDESIGNVIEW
      ) {
        setSelectedTab(0);
      }
    } else if (!shouldShowLayersIngredientPanel && !isStayOnEditGroup) {
      handleBackToPreviousScreen(ELayersView.LAYERDESIGNVIEW)();
      setHandleOnTabBackPress(null);
    }

    handleSelectedProteinLayerDetailChange(
      _selectedLayer?.type === LayerTypeDTO.Protein
        ? (cloneDeep(_selectedLayer.layerDetail) as ProteinLayerDTO)
        : null,
    );
    handleSelectedFragmentLayerDetailChange(
      _selectedLayer?.type === LayerTypeDTO.Fragment
        ? (cloneDeep(_selectedLayer.layerDetail) as FragmentLayerDTO)
        : null,
    );
  }, [_selectedLayer, _layers, shouldShowLayersIngredientPanel, selectedTabIndex]);

  useEffect(() => {
    if (
      _selectedLayer &&
      shouldShowLayersIngredientPanel &&
      _selectedLayer.type === LayerTypeDTO.ConditionList
    ) {
      setSelectedTab(1);
      _setLayerDesignSelectedTabIndex(ELayerDesignSelectedTabIndex.Layers);
    }
  }, [_layers?.length, shouldShowLayersIngredientPanel]);

  const resetProteinTreeStates = useCallback(() => {
    setParentNode(null);
    setPickerTreeControlState(TreeControlDefaultState);
  }, []);
  function getTab2Heading(): string {
    if (isNullOrUndefined(_selectedLayer)) {
      return '';
    }
    if (_selectedLayer.type == LayerTypeDTO.ConditionList) {
      return 'Conditions';
    }
    if (_selectedLayer.type == LayerTypeDTO.Seed) {
      return 'Seed';
    }
    if (_selectedLayer.type == LayerTypeDTO.Protein) {
      return 'Protein';
    }
    if (
      _selectedLayer.type == LayerTypeDTO.Screen ||
      _selectedLayer.type == LayerTypeDTO.Fragment
    ) {
      return 'Screen';
    }
    return 'Ingredients';
  }

  function isDataLoaded() {
    const ings = GetIngredients();
    const loaded =
      !isNullOrUndefined(_layers) &&
      !isNullOrUndefined(_selectedLayer) &&
      !isNullOrUndefined(ings) &&
      !isNullOrUndefined(ings.ingredients) &&
      ings.ingredients?.length > 0;
    if (loaded) {
      return !isNullOrUndefined(_layers[0]) && !isNullOrUndefined(_layers[0].layerDetail);
    }
  }

  const handleBackToPreviousScreen = useCallback(
    (contentView) => () => {
      disableLayersIngredientsPanel();
      setContentView(contentView);
    },
    [_selectedLayer, shouldShowLayersIngredientPanel],
  );

  const handleOnNodeSelect = useCallback(
    () => setContentView(ELayersView.SELECTPROTEIN),
    [],
  );

  const handleOnFragmentNodeSelect = useCallback(
    () => setContentView(ELayersView.SELECTFRAGMENTSCREEN),
    [],
  );

  const tabItems_: ITabItem[] = [
    {
      title: 'Explore',
      content: <LayersExplorer isSelected={selectedTabIndex == 0} />,
    },
    {
      content: (
        <div id="commandpanel-layerscontainer-tabitems" style={{ height: 'inherit' }}>
          {contentView === ELayersView.LAYERDESIGNVIEW && (
            <div
              id="commandpanel-layercontainer-layersdesignview-wrapper"
              className="h-full grid-layer-tabs w-100pr"
            >
              <LayersSelectionParent
                handleSelection={handleLayerType}
                layers={_layers}
                selectedParentLayerId={_selectedLayer?.id}
                isSubGrid={false}
              />
              {!isNullOrUndefined(_layers) && _layers?.length > 0 ? (
                <RMLayersTabs
                  headingTab1="Layers"
                  headingTab2={getTab2Heading()}
                  contentTab1={
                    isDataLoaded() ? (
                      <LayerInfoParent
                        setContentView={setContentView}
                        setEditExcludeGroupId={setEditExcludeGroupId}
                        containerMaxWellVolume={exp.containerMaxWellVolume}
                        isoRound={plate.isoRoundNumber}
                        isoPlate={plate.isoPreviousPlateID}
                        isoScores={plate.isoScores}
                      />
                    ) : (
                      <br />
                    )
                  }
                  contentTab2={
                    isDataLoaded() ? (
                      <LayersIngredients
                        setEditIngredientData={setEditIngredientData}
                        setContentView={setContentView}
                        setSelectedIngredient={setSelectIngredient}
                        setSelectedStocks={setSelectStock}
                        randomLyrActiveGroupId={randomLyrActiveGroupId}
                        setRandomLyrActiveGroupId={setRandomLyrActiveGroupId}
                        setEditSeedData={handleEditSeedDataChange}
                        editSeedData={editSeedData}
                        setSelectedTab={setSelectedTab}
                      />
                    ) : (
                      <br />
                    )
                  }
                  selectedTabIndex={selectedTab}
                  setSelectedTab={setSelectedTab}
                />
              ) : null}
            </div>
          )}
          {contentView === ELayersView.EDITINGREDIENTSVIEW && (
            <EditIngredients
              id="commandpanel-layercontainer-editingredientsview-wrapper"
              setContentView={setContentView}
              selectIngredient={selectIngredient}
              selectStock={selectStock}
              selectHighPHStock={highPHStock}
              editIngredientData={editIngredientData}
              resetStockValue={resetStocksValue}
              setSelectedTab={setSelectedTab}
              handleBackToPreviousScreen={handleBackToPreviousScreen}
            />
          )}
          {contentView === ELayersView.SELECTINGREDIENTSVIEW && (
            <SelectIngredients
              id="commandpanel-layercontainer-selectingredientsview-wrapper"
              setContentView={setContentView}
              setSelectIngredient={setSelectIngredient}
              setSelectedIng={setSelectedIngType}
              handleBackToPreviousScreen={handleBackToPreviousScreen}
              setSelectedStock={setSelectStock}
            />
          )}
          {contentView === ELayersView.SELECTSTOCKSVIEW && (
            <SelectStocks
              id="commandpanel-layercontainer-selectstocksview-wrapper"
              setContentView={setContentView}
              setSelectStock={setSelectStock}
              selectIngredient={selectIngredient}
              selectedIngType={selectedIngType}
              handleBackToPreviousScreen={handleBackToPreviousScreen}
            />
          )}

          {contentView === ELayersView.HIGHPHVIEW && (
            <SelectStocks
              id="commandpanel-layercontainer-highphview-wrapper"
              setContentView={setContentView}
              setSelectStock={setHighPHStock}
              selectIngredient={selectIngredient}
              lowPHStocks={selectStock}
              handleBackToPreviousScreen={handleBackToPreviousScreen}
            />
          )}
          {contentView === ELayersView.EXCLUDEINGREDIENT && (
            <ExcludeIngredients
              id="commandpanel-layercontainer-excludeingredient-wrapper"
              setContentView={setContentView}
              editExcludeGroupId={editExcludeGroupId}
              setEditExcludeGroupId={setEditExcludeGroupId}
              handleBackToPreviousScreen={handleBackToPreviousScreen}
            />
          )}

          {(contentView === ELayersView.EDITGROUPSVIEWADD ||
            contentView === ELayersView.EDITGROUPSVIEWEDIT) && (
            <>
              <EditGroup
                id="commandpanel-layercontainer-editgroup-wrapper"
                setContentView={setContentView}
                activeGroupId={randomLyrActiveGroupId}
                setRandomLyrActiveGroupId={setRandomLyrActiveGroupId}
                isEditGroupActive={contentView === ELayersView.EDITGROUPSVIEWEDIT}
                setSelectedTab={setSelectedTab}
                handleBackToPreviousScreen={handleBackToPreviousScreen}
                setIsStayOnEditGroup={setIsStayOnEditGroup}
              />
            </>
          )}
          {contentView === ELayersView.SELECTSEEDS && (
            <SelectSeedList
              id="commandpanel-layercontainer-selectseedlist-wrapper"
              setContentView={setContentView}
              setSelectedSeed={setSelectedSeed}
              setEditSeedData={handleEditSeedDataChange}
              editSeedData={editSeedData}
              setSelectedSeedName={setSelectedSeedName}
              handleBackToPreviousScreen={handleBackToPreviousScreen}
            />
          )}
          {contentView === ELayersView.EDITSEED && (
            <EditSeed
              id="commandpanel-layercontainer-editseed-wrapper"
              setContentView={setContentView}
              selectedSeed={selectedSeed}
              editSeedData={editSeedData}
              setEditSeedData={handleEditSeedDataChange}
              selectedSeedName={selectedSeedName}
              containerMaxDropVol={exp.containerMaxDropVolume}
              setSelectedTab={setSelectedTab}
              handleBackToPreviousScreen={handleBackToPreviousScreen}
              shouldReturnToLayersExplorer={
                shouldReturnToLayersExplorerRef.current === _selectedLayer?.id
              }
              onApplyComplete={handleReturnToLayersExplorer}
              selectedSeedDeleted={selectedSeedDeleted}
            />
          )}
          {contentView === ELayersView.SELECTPROTEIN && (
            <SelectProteinList
              id="commandpanel-layercontainer-selectproteinlist-wrapper"
              setContentView={setContentView}
              parentNode={parentNode}
              handleBackToPreviousScreen={handleBackToPreviousScreen}
              resetProteinTreeStates={resetProteinTreeStates}
              setSelectedProtein={handleSelectedProteinLayerDetailChange}
              selectedProtein={selectedProteinLayerDetail}
            />
          )}
          {contentView === ELayersView.EDITPROTEIN && (
            <EditProtein
              id="commandpanel-layercontainer-editprotein-wrapper"
              setContentView={setContentView}
              containerMaxDropVolume={exp.containerMaxDropVolume}
              handleBackToPreviousScreen={handleBackToPreviousScreen}
              setSelectedTab={setSelectedTab}
              selectedProtein={selectedProteinLayerDetail}
              shouldReturnToLayersExplorer={
                shouldReturnToLayersExplorerRef.current === _selectedLayer?.id
              }
              onApplyComplete={handleReturnToLayersExplorer}
              selectedProteinDeleted={selectedProteinDeleted}
            />
          )}
          {contentView === ELayersView.PROTEINTREEPATH && (
            <SelectProteinTreePath
              id="commandpanel-layercontainer-proteinteepath-wrapper"
              setContentView={setContentView}
              setProteinPath={undefined}
              setParentNode={setParentNode}
              treeControlState={pickerTreeControlState}
              setTreeControlState={setPickerTreeControlState}
              handleBackToPreviousScreen={handleBackToPreviousScreen}
              handleOnNodeSelect={handleOnNodeSelect}
            />
          )}
          {contentView === ELayersView.SELECTSCREEN && (
            <SelectScreen
              id="commandpanel-layercontainer-selectscreen-wrapper"
              setContentView={setContentView}
              setSelectedScreen={setSelectedScreen}
              handleBackToPreviousScreen={handleBackToPreviousScreen}
            />
          )}
          {contentView === ELayersView.EDITSCREEN && (
            <EditScreen
              id="commandpanel-layercontainer-editscreen-wrapper"
              setContentView={setContentView}
              screen={getScreen()}
              selectedLotId={getSelectedLotId()}
              setSelectedTab={setSelectedTab}
              onScreenLotChange={onScreenLotChange}
            />
          )}
          {contentView === ELayersView.SELECTFRAGMENTSCREEN && (
            <SelectFragmentScreen
              id="commandpanel-layercontainer-selectfragmentscreen-wrapper"
              setContentView={setContentView}
              parentNode={parentNode}
              handleBackToPreviousScreen={handleBackToPreviousScreen}
              resetFragmentTreeStates={resetProteinTreeStates}
              selectedFragmentLayer={selectedFragmentLayerDetail}
              setSelectedFragmentLayer={handleSelectedFragmentLayerDetailChange}
              setSelectedTab={setSelectedTab}
            />
          )}
          {contentView === ELayersView.FRAGMENTTREEPATH && (
            <SelectFragmentTreePath
              id="commandpanel-layercontainer-selectfragmenttreepath-wrapper"
              setParentNode={setParentNode}
              treeControlState={pickerTreeControlState}
              setTreeControlState={setPickerTreeControlState}
              handleBackToPreviousScreen={handleBackToPreviousScreen}
              handleOnNodeSelect={handleOnFragmentNodeSelect}
            />
          )}
          {contentView === ELayersView.EDITFRAGMENT && (
            <EditFragment
              setContentView={setContentView}
              selectedFragmentLayer={selectedFragmentLayerDetail}
              containerMaxDropVolume={exp.containerMaxDropVolume}
              handleBackToPreviousScreen={handleBackToPreviousScreen}
              setHandleOnTabBackPress={setHandleOnTabBackPress}
              shouldReturnToLayersExplorer={
                shouldReturnToLayersExplorerRef.current === _selectedLayer?.id
              }
              onApplyComplete={handleReturnToLayersExplorer}
              selectedFragmentDeleted={selectedFragmentDeleted}
            />
          )}
        </div>
      ),
      title: 'Layers Design',
    },
  ];

  return (
    <LayersContainerContextProvider
      treeControlState={treeControlState}
      setTreeControlState={setTreeControlState}
    >
      <BaseSidePanel
        tabItems={tabItems_}
        headerTitle={mainPanelHeading}
        headerVariant={EPanelHeaderVariant.TabsHeader}
        defaultTab={selectedTabIndex}
        setSelectedTabIndex={_setLayerDesignSelectedTabIndex}
      />
    </LayersContainerContextProvider>
  );

  function onScreenLotChange(lotId: number) {
    setSelectedLotId(lotId);
  }

  function getSelectedLotId() {
    if (selectedScreen) {
      const lot = selectedScreen.screenLot.find((l) => l.id == selectedLotId);
      return lot ? lot.id : selectedScreen.screenLot[0].id;
    }
    const screenLayer = _selectedLayer.layerDetail as ScreenLayerDTO;
    let lot: ScreenLotDTO = undefined;
    if (!isNullOrUndefined(screenLayer.screenLot) && isNullOrUndefined(selectedScreen)) {
      lot = screenLayer.screenLot;
      return lot.id;
    }

    if (!isNullOrUndefined(screenLayer.screenLot) && !isNullOrUndefined(selectedScreen)) {
      lot = selectedScreen.screenLot.find((sl) => sl.id === screenLayer.screenLot.id);
      if (!isNullOrUndefined(lot)) {
        return lot.id;
      }
    }
    if (!isNullOrUndefined(selectedScreen)) {
      lot = selectedScreen.screenLot.find((sl) => sl.initialLot === true);
      return lot.id;
    }
    return undefined;
  }

  function getScreen(): ScreenDTO {
    if (!isNullOrUndefined(selectedScreen)) {
      return selectedScreen;
    }

    if (
      !isNullOrUndefined(_selectedLayer) &&
      !isNullOrUndefined(_selectedLayer.layerDetail)
    ) {
      const screenLyr = _selectedLayer.layerDetail as ScreenLayerDTO;
      const screen = GetScreenById(screenLyr.screenLot?.experimentId);
      return screen;
    }
    return undefined;
  }
}

export default withTabBackButton(LayersContainer);
